import { Button } from 'devextreme-react/button';
import { Item, Toolbar } from 'devextreme-react/toolbar';
import DataGrid, { Column, Editing, Format, Summary, TotalItem } from 'devextreme-react/data-grid';
import { formatNumber } from 'devextreme/localization';
import { useContext, useState } from 'react';
import { RetailOrderContext } from '../context/RetailOrderContext';
import { ChangeOrderProduct } from '../types/RetailOrderDetails';
import { ROCatalogProduct } from '../types/ROCatalogProduct';
import ChangeOrderProductPopup from './ChangeOrderProductPopup';
import { BusinessFlow, ROType } from '../../../../types/enums';
import { useGetCompany } from "../../../../api/company";

export default function RetailOrderProducts() {

    const { order, modifyOrder } = useContext(RetailOrderContext);
    const dealer = useGetCompany(order.details.dealerCompanyID);
    let [isPopupVisible, setPopupVisibility] = useState(false);

    let products = order.details.changeOrderProducts.map(x => x);

    return (
        <>
            <Toolbar className='button-toolbar'>
                <Item
                    visible={true}
                    location={'before'}
                >
                    <Button
                        text={order.details.roTypeID === ROType.CabinetMeasureOrder ? "Add New Labor" : "Add New Product"}
                        icon="Inscyth-Add"
                        elementAttr={{ 'data-testid': 'btn-add-new-product' }}
                        width={200}
                        onClick={() => {
                            setPopupVisibility(true);
                        }}>
                    </Button>
                </Item>
            </Toolbar>

            <ChangeOrderProductPopup
                visible={isPopupVisible}
                onPopupHidden={() => setPopupVisibility(false)}
                onProductsAdded={(newProducts: ROCatalogProduct[]) => {

                    let id = Math.max(0, ...products.map(x => x.roProductChangeRequestID)) ?? 0;

                    var newRows = newProducts.map(row => {
                        let qty = row.quantity;
                        if (dealer.data?.businessFlowID !== BusinessFlow.Lowes) {
                            qty = Math.round(qty);
                        }
                        return {
                            roProductChangeRequestID: ++id,
                            dealerSKU: row.sku,
                            dealerModelNumber: row.mfrModel,
                            dealerUOM: row.uom,
                            dealerPrice: row.dealerPrice,
                            cfiPrice: row.cfiPrice,
                            dealerQty: qty,
                            productID: row.productID,
                            thickness: row.thickness,
                            productType: row.productType,
                            itemDescription: row.extendedDescription ? row.description + ": " + row.extendedDescription : row.description,
                            productLineDescription: row.productLine
                        }
                    });

                    var updatedProducts = products.concat(newRows);
                    modifyOrder({ ...order.details, changeOrderProducts: updatedProducts });
                }}
            />

            <DataGrid
                className={'dx-card wide-card'}
                dataSource={products}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                showColumnLines={true}
                columnAutoWidth={false}
                keyExpr={'roProductChangeRequestID'}
                onRowRemoved={(e) => {
                    modifyOrder({ ...order.details, changeOrderProducts: products });
                }}
                onRowUpdated={(e) => {
                    if (dealer.data?.businessFlowID !== BusinessFlow.Lowes) {
                        e.data.dealerQty = Math.round(e.data.dealerQty);
                    }

                    modifyOrder({ ...order.details, changeOrderProducts: products });
                }}
            >
                <Editing
                    mode="cell"
                    confirmDelete={true}
                    allowDeleting={true}
                    allowUpdating={true}
                />

                <Column
                    dataField={'itemDescription'}
                    caption={'Item Description'}
                    dataType="string"
                    width={300}
                    allowEditing={false}
                />
                <Column
                    dataField={'thickness'}
                    caption={'Thickness'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'productType'}
                    caption={'Product Type'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'productLineDescription'}
                    caption={'Product Line'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'dealerSKU'}
                    caption={'SKU'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'dealerUOM'}
                    caption={'UOM'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'dealerQty'}
                    caption={'Qty'}
                    dataType="number"
                    width={115}
                    editorOptions={{
                        showSpinButtons: true,
                        min: 0,
                        format: "##0.###",
                    }}
                    showEditorAlways={true}
                    cssClass="required"
                />
                <Column
                    dataField={'cfiPrice'}
                    caption={'Price'}
                    dataType="number"
                    allowEditing={false}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
                <Column
                    dataField={'cfiExtendedPrice'}
                    caption={'Extended Price'}
                    dataType="number"
                    width={120}
                    allowEditing={false}
                    calculateCellValue={(row: ChangeOrderProduct) => {
                        return row.cfiPrice * row.dealerQty
                    }}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
                <Summary>
                    <TotalItem
                        column="cfiExtendedPrice"
                        summaryType="sum"
                        customizeText={(data: any) => 'Total: ' + formatNumber(data.value, {
                            type: 'currency',
                            precision: 2
                        })}
                    />
                </Summary>
            </DataGrid>
        </>
    );
}
